
import { LinearProgress } from '@mui/material';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import Lottie from 'react-lottie-player';
import * as animationData from './transformation.json'
import * as animationDataSuccess from './Success.json'
import * as animationDataError from './Failure.json'

import "./LoaderAnimation.scss"
import { useNavigate, useParams } from 'react-router-dom';
import { ModalClose, InfoSvg } from '../../svgs/Svgs';
import SimulateService from '../../../../service/SimulateService';
import { showToastMessage } from '../../../../utils/commonFunctions';
import { ScenarioResponseType } from '../../types';


interface LoaderAnimationProps {
    loadingState: string | boolean;
    onClose: () => void;
    scenario: ScenarioResponseType | null;
    setLoadingState: (value: string) => void;
    errorMessage?: string;
}

const LoaderAnimation: React.FC<LoaderAnimationProps> = ({ loadingState, onClose, scenario, errorMessage, setLoadingState }) => {
    const params = useParams()
    const navigate = useNavigate()
    const [connectionStarted, setConnectionStarted] = useState<string>('Connect')
    const { mission_name, mission_id } = params
    const intervalRef = useRef<any>(null)

    const navigateToHud = () => {
        const scenarioId = scenario?.uniqueId
        const satelliteId = scenario?.satelliteGroups[0]?.satellites[0]?.satelliteId
        const satelliteGroupId = scenario?.satelliteGroups[0]?.satelliteGroupId
        if (!scenarioId || !satelliteId) return
        navigate(`${process.env.PUBLIC_URL}/${mission_name}/${mission_id}/quick-deploy/${scenarioId}/satGroupId/${satelliteGroupId}/satellite/${satelliteId}/hud`)
    }

    const getMessage = (loadingState) => {
        switch (loadingState) {
            case "deploying":
                return 'Deploying TrueTwin';
            case "download":
                return 'Waiting for connection...';
            case "setupComplete":
                return 'Setup Complete';
            default:
                return '';
        }
    }


    ///// GET CONFIG FILE
    const download_file = async () => {
        if (!scenario || !scenario?.hwInfo) return
        const button = document.querySelector('.export_file__button') as HTMLButtonElement
        button.disabled = true
        const scenario_id = scenario?.uniqueId
        const file = (scenario?.hwInfo || []).filter(item => item.category === 'Payload')?.[0]
        if (!file) return
        const credentials = sessionStorage.getItem("authentication");
        const fileId: string = file.uniqueId
        try {
            const response = SimulateService.downloadHwilConfigFile(mission_id, scenario_id, fileId, credentials)
            window.location.href = response;
        } catch (error) {
            showToastMessage('error', 'Failed to download asset')
        } finally {
            button.disabled = false
        }
    };

    const getAnimationData = useCallback(
        (loadingState) => {
            if (loadingState === "setupComplete") {
                return animationDataSuccess
            } else if (loadingState === "error") {
                return animationDataError
            } else {
                return animationData
            }
        }, [loadingState]
    )

    const checkRemoteConnection = async (
        scenario_id: string,
        reconnect: boolean,
        interval: NodeJS.Timeout | null
    ): Promise<{ connected: boolean; disconnected: boolean; timeout: boolean }> => {
        try {
            const response = await SimulateService.getHwilConnectStatus(mission_id, scenario_id, reconnect);
            const remote_applications = response?.data?.data;
            if (!remote_applications) return { connected: false, disconnected: true, timeout: false };
            const connected = remote_applications.every(obj => obj.status === 'CONNECTED');
            const timeout = remote_applications.some(obj => obj.status === 'TIMEOUT');
            const disconnected = !connected && !timeout;
            if (connected) {
                interval && clearTimeout(interval);
                intervalRef.current = null;
                setLoadingState('setupComplete');
                setConnectionStarted('Connected')
            } else if (timeout) {
                showToastMessage('error', 'Failed to Connect to device');
                interval && clearTimeout(interval);
                intervalRef.current = null;
                setConnectionStarted('Re Connect');
            }
            return { connected, disconnected, timeout };
        } catch (error) {
            showToastMessage('error', 'Failed to Connect to device');
            interval && clearTimeout(interval);
            intervalRef.current = null;
            setConnectionStarted('Re Connect');
            return { connected: false, disconnected: false, timeout: true };
        }
    }

    // TODO: Temporary solution — currently using polling to check remote app connection status.
    // This should be replaced with a WebSocket-based implementation as per AT-9947:
    // https://antaris.atlassian.net/browse/AT-9947
    const getRemoteAppStatus = async (scenarioData: ScenarioResponseType, reconnect: boolean) => {
        const scenario_id = scenarioData?.uniqueId
        const result = await checkRemoteConnection(scenario_id, reconnect, null)
        if (result.connected || result.timeout) return
        const pollStatus = async () => {
            await checkRemoteConnection(scenario_id, false, intervalRef.current)
            if (intervalRef.current) {
                intervalRef.current = setTimeout(pollStatus, 5000);
            }
        };
        intervalRef.current = setTimeout(pollStatus, 5000);
    };

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearTimeout(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [])

    return (
        <div className='h-100 w-100 d-flex align-items-center justify-content-between flex-column gap-2 pb-7'>
            <div className="d-flex align-items-end align-self-stretch justify-content-end">
                {(typeof loadingState === "string" && ["error", "download"].includes(loadingState)) &&
                    <button className="header__close__button" onClick={onClose}>
                        <ModalClose />
                    </button>
                }
            </div>
            <div className='d-flex align-items-center justify-content-between flex-column'>
                <span className="fs-20 mx-auto">Quick Deploy</span>
                <Lottie
                    key={String(loadingState)}
                    loop
                    animationData={getAnimationData(loadingState)}
                    play
                    style={{ width: 300, height: 300 }}
                />
            </div>

            <div className=' w-100 d-flex align-items-center justify-content-center flex-column gap-3'>
                {loadingState && <span className='step-message-headerText'>{getMessage(loadingState)}</span>}
                {(loadingState && loadingState === 'download' && scenario) &&
                    <Fragment>
                        <span className='step-message-headerText fs-16'>
                            Download and setup SDK to establish connection.
                        </span>
                        <div className='connect_info_text_message'>
                            <InfoSvg />
                            <span>
                                Ensure the Payload application is running in order to establish connection.
                            </span>
                        </div>
                        <div className='d-flex align-items-start justify-content-center flex-row gap-4'>
                            <button
                                disabled={!scenario}
                                className="footer__next__button next export_file__button" onClick={() => {
                                    download_file()
                                }}>
                                {`Download Config`}
                            </button>
                            <button
                                disabled={!scenario || (connectionStarted !== 'Connect' && connectionStarted !== 'Re Connect')}
                                className={`footer__close__button`} onClick={() => {
                                    const reconnect = connectionStarted === 'Re Connect';
                                    setConnectionStarted('Connecting..')
                                    getRemoteAppStatus(scenario, reconnect)
                                }}>
                                {connectionStarted}
                            </button>
                        </div>
                    </Fragment>

                }
                {(loadingState && loadingState === "setupComplete") &&
                    <div className='d-flex align-items-start justify-content-center flex-row gap-4'>
                        <button className="footer__next__button" onClick={navigateToHud}>
                            Continue
                        </button>
                    </div>

                }
                {(loadingState && loadingState === "error") &&
                    <span className='step-message-headerText fs-20'>{errorMessage}</span>
                }

            </div>
            {typeof loadingState === "string" && !["error", "setupComplete", "download"].includes(loadingState) && (
                <LinearProgress className='w-80' color="success" />
            )}
        </div>
    )
}

export default LoaderAnimation